import { useEffect, useState } from "react";
import { Package, X } from "lucide-react";
import { RepeatOrderBadge } from "./repeat_order";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Modal preview full-size mockup (dipakai OrdersCardList & ProductionMonitoring). */
// Iter 586 — `sources` opsional [{url,label}] (mis. beberapa mockup Shopee) + zoom sederhana.
//   Tanpa `sources` perilaku lama (mockup aktif nota Offline) tidak berubah.
export function MockupPreviewModal({ orderNumber, customerName, isRepeatOrder = false, repeatRef = "", onClose, sources }) {
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(1);
  const multi = Array.isArray(sources) && sources.length > 1;
  const [state, setState] = useState({ loading: true, err: "", notFound: false, blobUrl: "", isPdf: false, filename: "" });
  // Iter 287av — Nonce untuk force-refetch saat user klik "Coba Lagi".
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    let currentUrl = null;
    setState({ loading: true, err: "", notFound: false, blobUrl: "", isPdf: false, filename: "" });
    (async () => {
      try {
        // Iter 287av — cache-busting via querystring tambahan + "no-store" agar selalu
        // ambil versi aktif terbaru dari backend (bukan dari service worker / disk cache).
        const base = Array.isArray(sources) && sources[idx] ? sources[idx].url
          : `${API}/orders/${encodeURIComponent(orderNumber)}/mockup.preview`;
        const url = `${base}${base.includes("?") ? "&" : "?"}_=${Date.now()}`;
        const res = await fetch(url, { credentials: "include", cache: "no-store" });
        if (!res.ok) {
          if (cancelled) return;
          if (res.status === 404) {
            setState({ loading: false, err: "", notFound: true, blobUrl: "", isPdf: false, filename: "" });
            return;
          }
          const errMsg = res.status === 403
            ? "Anda tidak berwenang melihat mockup."
            : `Gagal memuat mockup (HTTP ${res.status}).`;
          setState({ loading: false, err: errMsg, notFound: false, blobUrl: "", isPdf: false, filename: "" });
          return;
        }
        const ct = res.headers.get("content-type") || "";
        const cd = res.headers.get("content-disposition") || "";
        const filenameMatch = cd.match(/filename="?([^";]+)"?/i);
        const filename = filenameMatch ? filenameMatch[1] : "mockup";
        const blob = await res.blob();
        currentUrl = URL.createObjectURL(blob);
        if (cancelled) { URL.revokeObjectURL(currentUrl); return; }
        setState({ loading: false, err: "", notFound: false, blobUrl: currentUrl, isPdf: ct === "application/pdf", filename });
      } catch (e) {
        if (!cancelled) setState({ loading: false, err: "Gagal memuat mockup: " + String(e?.message || e), notFound: false, blobUrl: "", isPdf: false, filename: "" });
      }
    })();
    return () => {
      cancelled = true;
      if (currentUrl) { try { URL.revokeObjectURL(currentUrl); } catch {} }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber, reloadNonce, idx]);
  useEffect(() => { setZoom(1); }, [idx]);

  const retry = () => setReloadNonce((n) => n + 1);

  return <div className="mockup-modal-backdrop" data-testid="mockup-preview-backdrop"
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="mockup-modal" role="dialog" aria-label={`Pratinjau mockup ${orderNumber}`}>
      <div className="mockup-modal-head">
        <div>
          <p className="eyebrow">MOCKUP</p>
          <h3>{customerName || "—"}</h3>
          <p className="muted small">{orderNumber}
            {multi && <span data-testid="mockup-preview-index"> · Mockup {idx + 1} dari {sources.length}</span>}
            {state.filename && state.filename !== "mockup" && <span data-testid="mockup-preview-version"> · Versi: {state.filename}</span>}</p>
          {isRepeatOrder && <div style={{marginTop:4}}>
            <RepeatOrderBadge active repeatRef={repeatRef} context="offline" size="compact"
              testid="mockup-preview-repeat-badge"/>
          </div>}
        </div>
        <div className="mockup-modal-actions">
          {multi && <>
            <button type="button" className="soft-btn small-btn" data-testid="mockup-preview-prev"
              onClick={() => setIdx((idx - 1 + sources.length) % sources.length)}>‹ Sebelumnya</button>
            <button type="button" className="soft-btn small-btn" data-testid="mockup-preview-next"
              onClick={() => setIdx((idx + 1) % sources.length)}>Berikutnya ›</button>
          </>}
          {state.blobUrl && !state.isPdf && <>
            <button type="button" className="soft-btn small-btn" aria-label="Perkecil" data-testid="mockup-zoom-out"
              disabled={zoom <= 1} onClick={() => setZoom(z => Math.max(1, z - 0.5))}>−</button>
            <span className="muted small" data-testid="mockup-zoom-level">{Math.round(zoom * 100)}%</span>
            <button type="button" className="soft-btn small-btn" aria-label="Perbesar" data-testid="mockup-zoom-in"
              disabled={zoom >= 3} onClick={() => setZoom(z => Math.min(3, z + 0.5))}>+</button>
          </>}
          {state.blobUrl && !state.isPdf && <a
            className="soft-btn small-btn"
            href={state.blobUrl}
            download={state.filename}
            data-testid="mockup-download"
          ><Package size={14} />Unduh</a>}
          <button className="icon-btn" onClick={onClose} data-testid="mockup-preview-close" aria-label="Tutup"><X size={20} /></button>
        </div>
      </div>
      <div className="mockup-modal-body">
        {state.loading && <div className="mockup-status" data-testid="mockup-preview-loading">Memuat mockup…</div>}
        {state.notFound && !state.loading && (
          <div className="mockup-status" data-testid="mockup-preview-empty">
            <p style={{margin:"0 0 10px"}}>Mockup belum tersedia</p>
            <button type="button" className="soft-btn small-btn" disabled aria-disabled="true"
              data-testid="mockup-preview-disabled">Pratinjau belum tersedia</button>
          </div>
        )}
        {state.err && !state.loading && (
          <div className="mockup-status mockup-error" data-testid="mockup-preview-error">
            <p style={{margin:"0 0 10px"}}>{state.err}</p>
            <button type="button" className="soft-btn small-btn" onClick={retry}
              data-testid="mockup-preview-retry">Coba Lagi</button>
          </div>
        )}
        {!state.loading && !state.err && !state.notFound && state.blobUrl && (
          state.isPdf
            ? <iframe title="Mockup PDF" src={`${state.blobUrl}#toolbar=0&navpanes=0&view=FitH`} className="mockup-preview-iframe" data-testid="mockup-preview-iframe" />
            : <div className="mockup-zoom-wrap" style={{ overflow: zoom > 1 ? "auto" : "hidden", justifyContent: zoom > 1 ? "flex-start" : "center" }} data-testid="mockup-zoom-wrap">
                <img src={state.blobUrl} alt={`Mockup ${orderNumber}`} className="mockup-preview-img" data-testid="mockup-preview-image"
                  onClick={() => setZoom(z => (z >= 3 ? 1 : z + 0.5))}
                  style={zoom > 1 ? { maxWidth: "none", maxHeight: "none", width: `${zoom * 100}%`, cursor: "zoom-in" } : { cursor: "zoom-in" }}/>
              </div>
        )}
      </div>
    </div>
  </div>;
}
